import { getDocumentType } from 'Components/Generic/Documents/Enum';
import { getOrgNameFromId } from 'Data/context/Organizations';
import { getUserNameFromId } from 'Data/context/Users';
import { getVendorNameFromId } from 'Data/context/Vendors';
import {
  getChargeAllocationStatusLabel,
  getTransactionCategory,
  getTransactionType,
} from 'Pages/Transactions/Enum';
import pdfMake from 'pdfmake/build/pdfmake';
import { vfs } from 'pdfmake/build/vfs_fonts';
import { formatMoney, roundToHundredth } from 'Util';
import { formatDate } from 'Util/dateUtil';
import { truncateText } from 'Util/pdfUtil/pdfMakeUtil';

pdfMake.vfs = vfs;

const styles = {
  pageHeader: { fontSize: 12, alignment: 'center', bold: true },
  footer: {
    fontSize: 9,
    alignment: 'left',
    margin: [12, 0, 12, 0],
  },
  right: {
    alignment: 'right',
  },
  subheader: {
    fontSize: 11,
    alignment: 'center',
    bold: true,
    margin: 4,
  },
  tableStyle: {
    margin: 6, //[0, 8, 0, 0],
    fontSize: 10,
    color: '#1c2127',
  },
  noteHeader: {
    italics: true,
    color: '#5f6b7c',
  },
  noteContent: {
    margin: [4, 0, 4, 8],
  },
  subTableStyle: {
    fontSize: 10,
    color: '#1c2127',
  },
  rightBoldHeader: { alignment: 'right', bold: true },
  subTableHeader: { alignment: 'center', bold: true },
  bold: {
    bold: true,
  },
  noContent: {
    italics: true,
    color: '#5f6b7c',
    alignment: 'center',
    margin: 10,
  },
};

const documentOptions = {
  pageOrientation: 'landscape',
  pageMargins: [18, 10, 18, 14],
  footer: (currentPage, pageCount) => {
    return {
      columns: [
        {
          text: `Generated ${formatDate(new Date())}`,
          style: 'footer',
        },
        ...(currentPage === 1
          ? []
          : [
              {
                text: `Page ${currentPage} of ${pageCount}`,
                style: ['footer', 'right'],
              },
            ]),
      ],
    };
  },
};

const mainTableLayout = {
  hLineWidth: function (i) {
    if (i === 1) {
      return 1;
    }
    return 0;
  },
  vLineWidth: function () {
    return 0;
  },
  hLineColor: function (i) {
    if (i === 1) {
      return 'gray';
    }
    return null;
  },
  vLineColor: () => {
    return null;
  },
  paddingLeft: () => {
    return 4;
  },
  paddingRight: () => {
    return 4;
  },
  paddingTop: () => {
    return 4;
  },
  paddingBottom: () => {
    return 4;
  },
};

const getSubDetailTable = (header, subTable, addHMargin = false) => {
  return {
    style: 'subTableStyle',
    table: {
      headerRows: 1,
      body: [
        [{ text: header, style: 'subheader' }],
        [
          {
            table: subTable,
            layout: {
              hLineWidth: function (i) {
                return 0;
              },
              vLineWidth: function () {
                return 0;
              },
              paddingLeft: (i) => {
                if (i === 0 && addHMargin) return 20;
                return 4;
              },
              paddingRight: (i, node) => {
                if (i === node.table.widths.length - 1 && addHMargin) return 20;
                return 4;
              },
              paddingTop: (i) => {
                return i === 0 ? 4 : 0;
              },
              paddingBottom: (i, node) => {
                return i === node.table.body.length ? 10 : 4;
              },
            },
          },
        ],
      ],
    },
    layout: mainTableLayout,
  };
};

const detailBoxWidthsNarrow = [100, 130];
const detailBoxWidths = [160, 198];

const notesBoxWidthsNarrow = [238];
const notesBoxWidths = [370];

const docsBoxWidthsNarrow = [320, 104, 60];
const docsBoxWidths = [200, 88, 60];

const getRequestDetailBox = (request, narrow = false) => {
  let tableRows = [
    [
      { text: 'Core', style: 'rightBoldHeader' },
      request.organization?.name ||
        getOrgNameFromId(request.parent_organization_id),
    ],
    [
      { text: 'Requester', style: 'rightBoldHeader' },
      request.requester?.full_name ||
        getUserNameFromId(request.requesting_user_id),
    ],
    [
      { text: 'Processor', style: 'rightBoldHeader' },
      request.processor?.full_name ||
        getUserNameFromId(request.processing_user_id),
    ],
    [
      { text: 'Date Submitted', style: 'rightBoldHeader' },
      formatDate(request.date_requested),
    ],
    [
      { text: 'Vendor', style: 'rightBoldHeader' },
      request.vendor?.name || getVendorNameFromId(request.vendor_id),
    ],
    [{ text: 'Description', style: 'rightBoldHeader' }, request.notes],
  ];

  return getSubDetailTable('Request Details', {
    widths: narrow ? detailBoxWidthsNarrow : detailBoxWidths,
    body: tableRows,
  });
};

const getMainTransactionDetailBox = (transaction) => {
  return getTransactionDetailBox(transaction, false, true);
};

const getTransactionDetailBox = (
  transaction,
  narrow = false,
  showAllDetails,
) => {
  let tableRows = [
    [{ text: 'ID', style: 'rightBoldHeader' }, transaction.id || ''],
    [
      { text: 'Category', style: 'rightBoldHeader' },
      getTransactionCategory(transaction.category),
    ],
    ...(showAllDetails && !transaction.request
      ? [
          [
            { text: 'Core', style: 'rightBoldHeader' },
            transaction.organization?.name ||
              getOrgNameFromId(transaction.parent_organization_id),
          ],
        ]
      : []),
    [
      { text: 'Transaction Type', style: 'rightBoldHeader' },
      getTransactionType(transaction.transaction_type),
    ],
    [
      { text: 'Transaction Date', style: 'rightBoldHeader' },
      formatDate(transaction.date_purchased),
    ],
    [
      { text: 'Purchaser', style: 'rightBoldHeader' },
      getUserNameFromId(transaction.purchased_by),
    ],
    [
      { text: 'Reference Number', style: 'rightBoldHeader' },
      transaction.reference_number,
    ],
    [
      { text: 'Confirmation Number', style: 'rightBoldHeader' },
      transaction.confirmation_number,
    ],
    ...(showAllDetails && !transaction.request
      ? [
          [
            { text: 'Vendor', style: 'rightBoldHeader' },
            transaction.vendor?.name ||
              getVendorNameFromId(transaction.vendor_id),
          ],
        ]
      : []),
    [{ text: 'Description', style: 'rightBoldHeader' }, transaction.notes],
  ];

  return getSubDetailTable('Transaction Details', {
    widths: narrow ? detailBoxWidthsNarrow : detailBoxWidths,
    body: tableRows,
  });
};

const getNotesBox = (notesList, narrow = false) => {
  let noteListContent = !notesList?.length
    ? [
        [
          {
            text: 'No Notes',
            style: 'noContent',
          },
        ],
      ]
    : notesList
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .reduce((tableContent, note) => {
          tableContent.push([
            {
              style: 'noteHeader',
              text: [
                `${formatDate(note.createdAt)} `,
                'by',
                {
                  text: ` ${getUserNameFromId(note.user_id)}`,
                  style: 'bold',
                },
              ],
            },
          ]);

          tableContent.push([{ text: note.content, style: 'noteContent' }]);

          return tableContent;
        }, []);

  return getSubDetailTable('Notes', {
    widths: narrow ? notesBoxWidthsNarrow : notesBoxWidths,
    body: noteListContent,
  });
};

const getDocumentsBox = (docList, narrow = false) => {
  let docListContent = !docList?.length
    ? [
        [
          {
            text: 'No Documents',
            style: 'noContent',
            colSpan: 3,
          },
        ],
      ]
    : docList.reduce(
        (tableContent, doc) => {
          tableContent.push([
            {
              text: truncateText(doc.original_name, {
                maxWidth: narrow ? docsBoxWidthsNarrow[0] : docsBoxWidths[0],
                fontSize: 10,
              }),
            },
            {
              text: truncateText(getDocumentType(doc.type), {
                maxWidth: narrow ? docsBoxWidthsNarrow[1] : docsBoxWidths[1],
                fontSize: 10,
              }),
            },
            { text: formatDate(doc.createdAt), style: { alignment: 'center' } },
          ]);
          return tableContent;
        },
        [
          [
            { text: 'Name', style: 'bold' },
            { text: 'Type', style: 'bold' },
            { text: 'Upload Date', style: 'subTableHeader' },
          ],
        ],
      );

  return getSubDetailTable('Documents', {
    widths: narrow ? docsBoxWidthsNarrow : docsBoxWidths,
    body: docListContent,
  });
};

const getLineItemsBox = (lineItemList, shippingAmount) => {
  let grandTotal =
    lineItemList &&
    lineItemList.reduce((acc, lineItem) => {
      if (lineItem.unit_price && lineItem.quantity) {
        return acc + lineItem.unit_price * lineItem.quantity;
      } else {
        return acc;
      }
    }, +shippingAmount || 0);

  let colWidths = [100, 190, 128, 60, 40, 8, 60, 8, 60];

  let linesContent;
  if (!lineItemList?.length) {
    linesContent = [
      [
        {
          text: 'No Line Items',
          style: 'noContent',
          colSpan: 9,
        },
      ],
    ];
  } else {
    linesContent = lineItemList.reduce(
      (tableContent, lineItem) => {
        tableContent.push([
          {
            text: truncateText(lineItem.vendor_catalog_number, {
              maxWidth: colWidths[0],
              fontSize: 10,
            }),
          },
          {
            text: truncateText(lineItem.description, {
              maxWidth: colWidths[1],
              fontSize: 10,
            }),
          },
          {
            text: truncateText(lineItem.comment, {
              maxWidth: colWidths[2],
              fontSize: 10,
            }),
          },
          { text: lineItem.measure_unit, noWrap: true },
          { text: lineItem.quantity, style: { alignment: 'center' } },
          { text: '$', style: { alignment: 'right' } },
          {
            text: formatMoney(lineItem.unit_price),
            style: { alignment: 'right' },
          },
          { text: '$', style: { alignment: 'right' } },
          {
            text:
              lineItem.unit_price && lineItem.quantity
                ? formatMoney(lineItem.unit_price * lineItem.quantity)
                : '',
            style: { alignment: 'right' },
          },
        ]);
        return tableContent;
      },
      [
        [
          { text: 'Cat #', style: 'bold' },
          { text: 'Description', style: 'bold' },
          { text: 'Comment', style: 'bold' },
          { text: 'Unit', style: 'bold' },
          { text: 'Qty', style: 'subTableHeader' },
          { text: 'Unit Price', style: 'rightBoldHeader', colSpan: 2 },
          {},
          { text: 'Total Price', style: 'rightBoldHeader', colSpan: 2 },
          {},
        ],
      ],
    );

    // Add space between lines and totals (hacky...)
    linesContent.push([{}, {}, {}, {}, {}, {}, {}, {}, {}]);
    linesContent.push([{}, {}, {}, {}, {}, {}, {}, {}, {}]);

    if (shippingAmount) {
      linesContent.push([
        {},
        {},
        {},
        {},
        {},
        {},
        { text: 'Shipping', style: 'rightBoldHeader' },
        { text: '$', style: { alignment: 'right' } },
        {
          text: formatMoney(+shippingAmount),
          style: 'rightBoldHeader',
        },
      ]);
    }

    linesContent.push([
      {},
      {},
      {},
      {},
      {},
      {},
      { text: 'Total', style: 'rightBoldHeader' },
      { text: '$', style: { alignment: 'right' } },
      { text: formatMoney(grandTotal), style: 'rightBoldHeader' },
    ]);

    return getSubDetailTable(
      'Line Items',
      {
        widths: colWidths,
        body: linesContent,
      },
      true,
    );
  }
};

const getChargeAllocationsBox = (chargeAllocationList) => {
  let grandTotal = roundToHundredth(
    chargeAllocationList.reduce((acc, ca) => {
      return ca.display_amount ? acc + +ca.display_amount : acc;
    }, 0),
  );

  let colWidths = [154, 154, 268, 8, 60, 50];

  let linesContent;
  if (!chargeAllocationList?.length) {
    linesContent = [
      [
        {
          text: 'No Transaction Lines',
          style: 'noContent',
          colSpan: 6,
        },
      ],
    ];
  } else {
    linesContent = chargeAllocationList.reduce(
      (tableContent, chargeAllocation) => {
        tableContent.push([
          {
            text: truncateText(
              chargeAllocation.charge_account
                ? `${chargeAllocation.charge_account.code} ${chargeAllocation.charge_account.name}`
                : '',
              {
                maxWidth: colWidths[0],
                fontSize: 10,
              },
            ),
          },
          {
            text: truncateText(
              chargeAllocation.expense_code
                ? `${chargeAllocation.expense_code.expense_code} ${chargeAllocation.expense_code.description}`
                : '',
              {
                maxWidth: colWidths[1],
                fontSize: 10,
              },
            ),
          },
          {
            text: truncateText(chargeAllocation.description, {
              maxWidth: colWidths[2],
              fontSize: 10,
            }),
          },
          { text: '$', style: { alignment: 'right' } },
          {
            text: formatMoney(chargeAllocation.display_amount),
            style: { alignment: 'right' },
          },
          {
            text: getChargeAllocationStatusLabel(chargeAllocation.status),
            style: { alignment: 'center' },
          },
        ]);
        return tableContent;
      },
      [
        [
          { text: 'Act/Proj', style: 'bold' },
          { text: 'Expense Code', style: 'bold' },
          { text: 'Description', style: 'bold' },
          { text: 'Amount', style: 'subTableHeader', colSpan: 2 },
          {},
          { text: 'Status', style: 'subTableHeader' },
        ],
      ],
    );

    // Add space between lines and totals (hacky...)
    linesContent.push([{}, {}, {}, {}, {}, {}]);
    linesContent.push([{}, {}, {}, {}, {}, {}]);

    linesContent.push([
      {},
      {},
      { text: 'Total', style: 'rightBoldHeader' },
      { text: '$', style: { alignment: 'right' } },
      { text: formatMoney(grandTotal), style: 'rightBoldHeader' },
      {},
    ]);
  }

  return getSubDetailTable(
    'Transaction Lines',
    {
      widths: colWidths,
      body: linesContent,
    },
    true,
  );
};

const getRequestDetailsTable = (request) => {
  let cols = 1 + (request?.transactions?.length || 1);
  if (cols > 3) {
    throw new Error({
      message: 'Cannot handle more than 2 transactions at this time!',
    });
  }
  let narrowSubTables = cols === 3;

  let transactions = request?.transactions?.map((transaction) =>
    getTransactionDetailBox(transaction, narrowSubTables),
  );
  if (transactions.length < 1) {
    transactions.push({});
  }

  return {
    style: 'tableStyle',
    table: {
      dontBreakRows: true,
      body: [
        [getRequestDetailBox(request, narrowSubTables), ...transactions],
        [
          ...(narrowSubTables
            ? [
                {
                  ...getDocumentsBox(request.documents, narrowSubTables),
                  colSpan: 2,
                },
                {},
              ]
            : [getDocumentsBox(request.documents, narrowSubTables)]),
          getNotesBox(request.request_notes, narrowSubTables),
        ],
        [
          {
            ...getLineItemsBox(request.line_items, request.shipping_amount),
            colSpan: cols,
          },
        ],
      ],
    },
  };
};

const getTransactionDetailsTable = (transaction) => {
  return {
    style: 'tableStyle',
    table: {
      dontBreakRows: true,
      body: [
        [
          getMainTransactionDetailBox(transaction),
          transaction.request ? getRequestDetailBox(transaction.request) : {},
        ],
        [
          getDocumentsBox(transaction.documents),
          getNotesBox(transaction.transaction_notes),
        ],
        ...(transaction.category === 'EXPENSE'
          ? [
              [
                {
                  ...getLineItemsBox(
                    transaction.line_items,
                    transaction.shippings[0]?.amount,
                  ),
                  colSpan: 2,
                },
              ],
            ]
          : []),
        [
          {
            ...getChargeAllocationsBox(transaction.charge_allocations),
            colSpan: 2,
          },
        ],
      ],
    },
  };
};

const getDocDefinition = (title, bodyContent) => {
  return {
    info: {
      title: title,
      subject: 'BOS Transaction Details',
      creator: 'BOS using pdfmake',
    },
    ...documentOptions,
    content: [
      {
        text: {
          text: title,
          style: 'pageHeader',
        },
      },
      bodyContent,
    ],
    styles: styles,
  };
};

const getTitleForRequestPDF = (request) => {
  return `Request ${request.id}`;
};

export const generatePDFforRequest = (
  request,
  download = false,
  callback = () => {},
  onError = () => {},
) => {
  if (!request) {
    onError({ message: 'No request provided' });
    return;
  }

  try {
    let title = getTitleForRequestPDF(request);
    let requestBody = getRequestDetailsTable(request);
    let docDefinition = getDocDefinition(title, requestBody);

    download && pdfMake.createPdf(docDefinition).download(title + '.pdf');
    !download && pdfMake.createPdf(docDefinition).open();

    callback();
  } catch (e) {
    onError(e);
  }
};

const getTitleForTransactionPDF = (transaction) => {
  return `Transaction ${transaction.id}`;
};

export const generatePDFforTransaction = (
  transaction,
  download = false,
  callback = () => {},
  onError = () => {},
) => {
  if (!transaction) {
    onError({ message: 'No transaction provided' });
    return;
  }

  try {
    let title = getTitleForTransactionPDF(transaction);
    let transactionBody = getTransactionDetailsTable(transaction);
    let docDefinition = getDocDefinition(title, transactionBody);

    download && pdfMake.createPdf(docDefinition).download(title + '.pdf');
    !download && pdfMake.createPdf(docDefinition).open();

    callback();
  } catch (e) {
    onError(e);
  }
};
